/**
 * Carrier-solution identification + derivation helpers.
 *
 * "Carrier" = a standard IV diluent the drug is mixed into (NaCl, Glucose,
 * Ringer's etc.) — distinct from drug × drug Y-site mixing. The source
 * matrix (USB LL0034-V07 and per-institution equivalents) carries these
 * carrier verdicts in the same `cells` map as drug × drug verdicts; this
 * module is just the lens that splits them out for the Carriers tab.
 *
 * Identification is by explicit ID list, NOT by ATC class — ATC B05
 * ("Blood substitutes and perfusion solutions") technically covers
 * carriers but also covers KCl/electrolytes that are NOT carriers; an
 * explicit list keeps surprises to "we added a new carrier" (rare,
 * one config touch) instead of "an electrolyte showed up in the
 * carrier strip" (silent miscategorisation, easy to ship by accident).
 *
 * Sorted by clinical-room frequency, not alphabetically: NaCl 0.9% +
 * Glucose 5% are the daily defaults; specialty diluents come after.
 */

import type { CompatibilityBoardState, CompatibilityDrug } from '@frontend/components/ysite/types'

/**
 * Carrier drug IDs the Carriers tab uses as columns. Order is the
 * render order in the tab — clinical-room frequency, not alphabetical.
 * ADD a new carrier here AND ensure the matrix fixture defines verdicts
 * against it; an unknown id surfaces as a missing column at runtime
 * (the lookup returns undefined and the tab silently drops it).
 */
export const CARRIER_DRUG_IDS: readonly string[] = [
  'NaCl 0.9%',
  'Glucose 5%',
  'Glucose 10%',
  'Glucose 20%',
  'NaCl 3%',
  'Ringeracetat',
  'Ringerlactat',
]

const CARRIER_ID_SET = new Set<string>(CARRIER_DRUG_IDS)

/** True when the drug is one of the recognised IV carrier solutions. */
export function isCarrier(drug: CompatibilityDrug): boolean {
  return CARRIER_ID_SET.has(drug.id)
}

/**
 * Carrier-axis CompatibilityDrug list, in CARRIER_DRUG_IDS render order.
 * Drugs absent from the matrix (e.g. an institution that doesn't stock
 * Ringer's lactate) are filtered out silently — the tab renders fewer
 * columns rather than empty placeholders.
 */
export function getCarrierDrugs(state: CompatibilityBoardState): CompatibilityDrug[] {
  const byId = new Map<string, CompatibilityDrug>()
  for (const d of state.drugs) byId.set(d.id, d)
  const result: CompatibilityDrug[] = []
  for (const id of CARRIER_DRUG_IDS) {
    const d = byId.get(id)
    if (d) result.push(d)
  }
  return result
}

/**
 * Non-carrier drugs (the row axis for the Carriers tab). Sentinels are
 * stripped — they exist to carry blanket verdicts in the main matrix and
 * have nothing to say about specific carriers.
 */
export function getNonCarrierDrugs(state: CompatibilityBoardState): CompatibilityDrug[] {
  return state.drugs.filter((d) => !d.sentinel && !isCarrier(d))
}
